export class ProjectionTimeoutError extends Error {
  constructor() {
    super("projection timeout");
    this.name = "ProjectionTimeoutError";
  }
}

export function readAggregateId(envelope: { aggregate_id?: string; id?: string }): string {
  if (!envelope.aggregate_id) {
    throw new Error("missing aggregate_id");
  }
  return envelope.aggregate_id;
}

type PollOptions = {
  tries?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
};

function defaultSleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function pollUntilProjected<T>(read: () => Promise<T>, options?: PollOptions): Promise<T> {
  const tries = options?.tries ?? 10;
  const intervalMs = options?.intervalMs ?? 1500;
  const sleep = options?.sleep ?? defaultSleep;

  for (let attempt = 0; attempt < tries; attempt += 1) {
    try {
      return await read();
    } catch {
      if (attempt < tries - 1) {
        await sleep(intervalMs);
      }
    }
  }

  throw new ProjectionTimeoutError();
}

export async function pollUntilListed<T extends { id: string }>(
  list: () => Promise<T[]>,
  aggregateId: string,
  options?: PollOptions
): Promise<T> {
  return pollUntilProjected(async () => {
    const rows = await list();
    const found = rows.find((row) => row.id === aggregateId);
    if (!found) {
      throw new Error("not listed");
    }
    return found;
  }, options);
}
